import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import backend from "../../backend";
import CierreFacturacionModal from "../../components/design-system/CierreFacturacionModal";
import CierreParcialModal from "../../components/design-system/CierreParcialModal";
import PromoverGuiaModal from "../../components/design-system/PromoverGuiaModal";
import AplicarOCModal from "../../components/design-system/AplicarOCModal";
import FacturarGuiaCompletaModal from "../../components/design-system/FacturarGuiaCompletaModal";
import FacturarModal from "../../components/design-system/FacturarModal";

// ─── Helpers de formato ────────────────────────────────────────────────────
export const fmtCLP = (n) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n || 0);

export const fmtTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

export const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit" });
};

export const fmtDateTime = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ─── Estilos compartidos ───────────────────────────────────────────────────
export const expandWrap = {
  // clamp: 20px de margen lateral en desktop, ~8px en móvil (360-430px)
  // Estándar 28-ago: tarjeta #232323, radio 12, borde #2e2e2e.
  margin: "0 clamp(8px, 2.5vw, 20px) 20px",
  background: "#232323",
  border: "1px solid #2e2e2e",
  borderRadius: 12,
  overflow: "hidden",
};

export const filterRow = {
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
  padding: "10px 12px",
  borderBottom: "1px solid #2e2e2e",
  background: "#1f1f1f",
};

export const stateMessage = (color = "#666") => ({
  padding: "20px",
  textAlign: "center",
  color,
  fontSize: 12,
});

export const tableTH = {
  // Estándar 28-ago: th 10px/700 #999 sobre #1f1f1f.
  padding: "5px 10px",
  textAlign: "left",
  fontSize: 10,
  fontWeight: 700,
  color: "#999",
  textTransform: "uppercase",
  letterSpacing: ".06em",
  borderBottom: "1px solid #2e2e2e",
  background: "#1f1f1f",
  whiteSpace: "nowrap",
};

export const tableTD = {
  padding: "6px 10px",
  fontSize: 11,
  color: "#bbb",
  borderBottom: "1px solid #1f1f1f",
  whiteSpace: "nowrap",
};

// Chip estándar 28-ago: activo = color semántico en texto/borde sobre
// `${color}22`; inactivo = #1e1e1e / #999 / borde #333, radio 6.
export const chipStyle = (active, activeColor, color) => ({
  background: active ? color + "22" : "#1e1e1e",
  color: active ? color : "#999",
  padding: "5px 12px",
  borderRadius: 6,
  fontSize: 11,
  fontWeight: 700,
  border: active ? `1px solid ${color}` : "1px solid #333",
  cursor: "pointer",
  userSelect: "none",
  whiteSpace: "nowrap",
});

// ─── Card top (cards arriba de /produccion) ────────────────────────────────
export const Card = ({ label, value, subtitle, accentColor, onClick, isActive, distribution }) => (
  <div
    onClick={onClick}
    className="prod-card tap-card"
    style={{
      // Estándar 28-ago: tarjeta #232323 radio 12; activa mantiene el acento en el borde.
      background: isActive ? "#282828" : "#232323",
      border: `1px solid ${isActive ? accentColor : "#2e2e2e"}`,
      borderRadius: 12,
      padding: "16px 18px",
      cursor: "pointer",
      transition: "border-color .15s, background .15s, transform .15s, opacity .15s",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      minHeight: 130,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "#8a8a8a",
          textTransform: "uppercase",
          letterSpacing: ".05em",
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 11, color: isActive ? accentColor : "#555" }}>
        {isActive ? "▼" : "▶"}
      </span>
    </div>

    {/* tabular-nums: los montos no "bailan" al recalcular (lenguaje móvil) */}
    <div style={{ fontSize: 24, fontWeight: 700, color: "#fff", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </div>

    <div style={{ fontSize: 11, color: "#cbd5e1", marginTop: -2 }}>{subtitle}</div>

    {distribution && distribution.length > 0 && (
      <div
        className="prod-dist"
        style={{
          display: "flex",
          gap: 2,
          height: 4,
          borderRadius: 2,
          overflow: "hidden",
          background: "#0d0d0d",
          marginTop: "auto",
        }}
        title={distribution.map((d) => `${d.label}: ${d.value}`).join(" · ")}
      >
        {distribution.map((d, i) => (
          <div key={i} style={{ flex: (d.value || 0) + 0.0001, background: d.color }} />
        ))}
      </div>
    )}
  </div>
);

// CerrarOTModal y EditarCierreOTModal eliminados — usar CierreFacturacionModal
// importado desde src/components/design-system/.

// ─── Estado de OT (badge usado en DetalleOCs) ──────────────────────────────
export const EstadoOT = ({ status, factura }) => {
  let label = "⏳ pendiente";
  let color = "#888";
  if (factura) { label = "📦 facturada"; color = "#2ecc71"; }
  else if (status === "delivered") { label = "✓ entregada"; color = "#2ecc71"; }
  else if (status === "ready") { label = "✓ lista"; color = "#2ecc71"; }
  else if (status === "working") { label = "⚙ en proceso"; color = "#3498db"; }
  else if (status === "approved") { label = "⏳ aprobada"; color = "#888"; }
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: "2px 7px",
        borderRadius: 9,
        background: color + "22",
        color,
        border: `1px solid ${color}44`,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

// ─── DetalleOCs ────────────────────────────────────────────────────────────
export const DetalleOCs = ({ items, loading, error, onWorkOrderClosed }) => {
  const [openId, setOpenId] = useState(null);
  const [ocFilter, setOcFilter] = useState("all"); // all | con_oc | verbal — chips clickeables
  const [search, setSearch] = useState(""); // buscar por OC/OT/guía/factura/cliente/pieza
  const [closeTarget, setCloseTarget] = useState(null); // F5.8 — OT a cerrar
  const [editTarget, setEditTarget]   = useState(null); // Fase 1 — OT a editar cierre
  const [partialTarget, setPartialTarget] = useState(null); // cierre parcial multi-item
  const [promoteTarget, setPromoteTarget] = useState(null); // promover guía → factura
  const navigate = useNavigate();

  if (loading && (!items || items.length === 0)) {
    return <div className="prod-expand" style={expandWrap}><div style={stateMessage()}>Cargando OCs activas…</div></div>;
  }
  if (error) {
    return <div className="prod-expand" style={expandWrap}><div style={stateMessage("#e74c3c")}>Error: {error}</div></div>;
  }
  if (!items || items.length === 0) {
    return <div className="prod-expand" style={expandWrap}><div style={stateMessage()}>No hay OCs activas</div></div>;
  }

  const conOC = items.filter((i) => i.clientOCNumber).length;
  const verbales = items.length - conOC;
  // Búsqueda libre: por N° de OC/pedido, N° de OC del cliente, N° de OT, guía,
  // factura, nombre del cliente o código/nombre de pieza. (Requerimiento Catalina.)
  const q = search.trim().toLowerCase();
  const matchCO = (co) => {
    if (!q) return true;
    const campos = [co.number, co.clientOCNumber, co.client?.name];
    (co.ots || []).forEach((o) => campos.push(o.number, o.guiaDespacho, o.factura, o.documentoTributario));
    (co.items || []).forEach((it) => campos.push(it.sku, it.name, it.desc));
    return campos.some((v) => v != null && String(v).toLowerCase().includes(q));
  };
  // Filtra la lista según el chip activo + la búsqueda.
  const baseShown =
    ocFilter === "con_oc" ? items.filter((i) => i.clientOCNumber)
    : ocFilter === "verbal" ? items.filter((i) => !i.clientOCNumber)
    : items;
  const shown = baseShown.filter(matchCO);

  return (
    <div className="prod-expand" style={expandWrap}>
      <div className="chips-scroll" style={filterRow}>
        <span className="chip-touch t-02" onClick={() => setOcFilter("all")} style={chipStyle(ocFilter === "all", "#fff", "#3498db")}>Todas ({items.length})</span>
        <span className="chip-touch t-02" onClick={() => setOcFilter("con_oc")} style={chipStyle(ocFilter === "con_oc", "#3498db", "#3498db")}>Con N° OC ({conOC})</span>
        <span className="chip-touch t-02" onClick={() => setOcFilter("verbal")} style={chipStyle(ocFilter === "verbal", "#a78bfa", "#a78bfa")}>Verbales ({verbales})</span>
        <div style={{ flex: 1, minWidth: 160, display: "flex", justifyContent: "flex-end" }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar OC / OT / guía / factura / cliente / pieza…"
            style={{
              width: "100%",
              maxWidth: 320,
              background: "#0f0f0f",
              border: `1px solid ${q ? "#3498db" : "#2e2e2e"}`,
              borderRadius: 6,
              padding: "5px 10px",
              fontSize: 11,
              color: "#fff",
              outline: "none",
            }}
          />
          {q && (
            <span
              onClick={() => setSearch("")}
              style={{ marginLeft: 6, alignSelf: "center", cursor: "pointer", color: "#999", fontSize: 13 }}
              title="Limpiar"
            >
              ✕
            </span>
          )}
        </div>
      </div>
      {q && (
        <div style={{ padding: "6px 12px", fontSize: 10, color: "#888", borderBottom: "1px solid #1f1f1f" }}>
          {shown.length} resultado{shown.length === 1 ? "" : "s"} para “{search.trim()}”
        </div>
      )}

      <div>
        {shown.map((co) => {
          const isOpen = openId === co._id;
          const otsCount = (co.ots || []).length;
          const otsListas = (co.ots || []).filter(
            (o) => o.status === "delivered" || o.status === "ready" || o.factura
          ).length;
          const pct = otsCount > 0 ? Math.round((otsListas / otsCount) * 100) : 0;
          return (
            <div key={co._id} style={{ borderBottom: "1px solid #1f1f1f" }}>
              <div
                onClick={() => setOpenId(isOpen ? null : co._id)}
                style={{
                  background: isOpen ? "#1f1f24" : "#1a1a1a",
                  padding: "8px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  rowGap: 6,
                  flexWrap: "wrap", // móvil: los badges bajan de línea en vez de rebalsar
                  cursor: "pointer",
                  borderBottom: isOpen ? "1px solid #2e2e2e" : "none",
                }}
              >
                <span style={{ fontSize: 11, color: "#999", width: 12 }}>
                  {isOpen ? "▼" : "▶"}
                </span>

                {co.number != null && (
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#f39c12",
                      background: "#f39c1218",
                      border: "1px solid #f39c1244",
                      padding: "2px 7px",
                      borderRadius: 4,
                      whiteSpace: "nowrap",
                    }}
                  >
                    OT {co.number}
                  </span>
                )}

                {co.clientOCNumber ? (
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#3498db",
                      background: "#3498db18",
                      border: "1px solid #3498db44",
                      padding: "2px 7px",
                      borderRadius: 4,
                      whiteSpace: "nowrap",
                    }}
                  >
                    N° OC {co.clientOCNumber}
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#a78bfa",
                      background: "#a78bfa18",
                      border: "1px solid #a78bfa44",
                      padding: "2px 7px",
                      borderRadius: 4,
                      whiteSpace: "nowrap",
                    }}
                  >
                    verbal · sin OC
                  </span>
                )}

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: "#fff",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {co.client?.name || <i style={{ color: "#999" }}>sin cliente</i>}
                  </span>
                  {(() => {
                    // Heurística "OC sin datos del Drive": solo señales reales de la CO.
                    // F4.4-fix2 pobló TODAS las OT.description con default sintético
                    // ("cotización [cliente] [number]"), así que ese campo ya no
                    // distingue OC con datos vs sin datos. Usar items_count y monto_total
                    // que sí provienen del Drive — cuando F4.6 sincronice, dejan de ser 0.
                    const hasNoItems = ((co.items_total_count ?? co.items_count) || 0) === 0;
                    const hasNoMonto = (co.monto_total || 0) === 0;
                    if (!(hasNoItems && hasNoMonto)) return null;
                    return (
                      <div
                        style={{
                          fontSize: 9,
                          color: "#f39c12",
                          marginTop: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          fontWeight: 400,
                        }}
                      >
                        <span>⚠</span>
                        <span>Datos del Drive pendientes</span>
                      </div>
                    );
                  })()}
                </div>

                <span style={{ fontSize: 11, color: "#cbd5e1", whiteSpace: "nowrap" }}>
                  {fmtDate(co.createdAt)}
                </span>

                <span
                  style={{ fontSize: 12, fontWeight: 700, color: "#2ecc71", whiteSpace: "nowrap", minWidth: 100, textAlign: "right" }}
                >
                  {fmtCLP(co.monto_total)}
                </span>

                {otsCount > 0 ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 90 }}>
                    <div style={{ width: 60, height: 4, background: "#2a2a2a", borderRadius: 2, overflow: "hidden" }}>
                      <div
                        style={{
                          height: "100%",
                          width: `${pct}%`,
                          background: pct === 100 ? "#2ecc71" : "#f39c12",
                        }}
                      />
                    </div>
                    <span style={{ fontSize: 10, color: pct === 100 ? "#2ecc71" : "#f39c12", fontWeight: 700 }}>
                      {otsListas}/{otsCount}
                    </span>
                  </div>
                ) : (
                  <span style={{ fontSize: 10, color: "#999", minWidth: 90, textAlign: "right" }}>
                    sin OTs
                  </span>
                )}
              </div>

              {isOpen && (
                <div style={{ background: "#101010" }}>
                  {/* SECCIÓN 1: Ítems de la OC */}
                  <div style={{ padding: "10px 12px" }}>
                    <div
                      style={{
                        fontSize: 10,
                        color: "#8a8a8a",
                        textTransform: "uppercase",
                        letterSpacing: ".06em",
                        marginBottom: 6,
                      }}
                    >
                      Ítems / piezas ({(co.items || []).length})
                      {(co.items_done_count || 0) > 0 && (
                        <span style={{ color: "#2ecc71", marginLeft: 8, fontWeight: 700 }}>
                          +{co.items_done_count} ya facturado/entregado
                          {(co.items_total_count || 0) > 0 ? ` · ${(co.items || []).length} de ${co.items_total_count} en producción` : ""}
                        </span>
                      )}
                    </div>
                    {(co.items || []).length === 0 ? (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#aaa",
                          fontStyle: "italic",
                          padding: "4px 0",
                        }}
                      >
                        Sin ítems en la CO (datos del Drive pendientes)
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th style={{ ...tableTH, width: 70 }}>N°</th>
                            <th style={{ ...tableTH, width: 130 }}>SKU</th>
                            <th style={tableTH}>Descripción</th>
                            <th style={{ ...tableTH, width: 90, textAlign: "right" }}>Cant</th>
                            <th style={{ ...tableTH, width: 90, textAlign: "right" }}>$ Unit</th>
                            <th style={{ ...tableTH, width: 100, textAlign: "right" }}>Total</th>
                            <th style={{ ...tableTH, width: 130, textAlign: "right" }}>Cierre</th>
                          </tr>
                        </thead>
                        <tbody>
                          {co.items.map((it, idx) => ([
                            <tr key={idx}>
                              <td style={{ ...tableTD, fontFamily: "monospace", color: "#aaa" }}>
                                {it.sub_id || `—-${idx}`}
                              </td>
                              <td
                                style={{
                                  ...tableTD,
                                  fontFamily: "monospace",
                                  color: it.sku ? "#3498db" : "#555",
                                }}
                              >
                                {it.sku || "—"}
                              </td>
                              <td style={{ ...tableTD, color: "#fff", whiteSpace: "normal" }}>
                                {it.name || it.desc || (
                                  <i style={{ color: "#999" }}>(sin descripción)</i>
                                )}
                                {it.source === "temp" && (
                                  <span
                                    style={{
                                      marginLeft: 6,
                                      fontSize: 9,
                                      color: "#a78bfa",
                                      background: "#a78bfa18",
                                      border: "1px solid #a78bfa44",
                                      padding: "1px 5px",
                                      borderRadius: 3,
                                      fontWeight: 700,
                                    }}
                                  >
                                    temp
                                  </span>
                                )}
                              </td>
                              <td
                                style={{ ...tableTD, textAlign: "right", color: "#aaa" }}
                              >
                                {it.qty || 0}
                              </td>
                              <td
                                style={{
                                  ...tableTD,
                                  textAlign: "right",
                                  color: it.pUnit ? "#aaa" : "#666",
                                }}
                              >
                                {it.pUnit ? fmtCLP(it.pUnit) : (
                                  <span
                                    title={
                                      it.piece || it.sku
                                        ? "SIN PRECIO: ni la cotización ni el catálogo lo tienen. Click para cargar precio en catálogo"
                                        : "Item sin SKU. Crear pieza en catálogo o ver detalle en /pulido"
                                    }
                                    style={{
                                      color: "#e74c3c",
                                      fontWeight: 700,
                                      fontSize: 10,
                                      cursor: "pointer",
                                      background: "#e74c3c18",
                                      border: "1px solid #e74c3c55",
                                      padding: "2px 6px",
                                      borderRadius: 3,
                                    }}
                                    onClick={e => {
                                      e.stopPropagation();
                                      if (it.sku) {
                                        navigate(`/pieces?search=${encodeURIComponent(it.sku)}`);
                                      } else if (it.piece) {
                                        navigate(`/pieces?search=${it.piece}`);
                                      } else {
                                        navigate("/pulido#items-pendientes");
                                      }
                                    }}
                                  >
                                    sin precio
                                  </span>
                                )}
                                {it.pUnit > 0 && (() => {
                                  const esCot = (it.priceSource || "").startsWith("cotizacion:");
                                  const cotN  = esCot ? it.priceSource.split(":")[1] : null;
                                  const color = esCot ? "#2ecc71" : it.pUnitFromCatalog ? "#a085f0" : "#888";
                                  return (
                                    <span
                                      title={
                                        esCot
                                          ? `Precio neto de la cotización N°${cotN} (la cotización manda sobre el catálogo)`
                                          : it.pUnitFromCatalog
                                            ? `Precio del catálogo al ${fmtDate(co.createdAt)}`
                                            : "Precio manual / de la OC del cliente"
                                      }
                                      style={{
                                        display: "inline-block",
                                        marginLeft: 6,
                                        fontSize: 9,
                                        fontWeight: 600,
                                        color,
                                        background: `${color}18`,
                                        border: `1px solid ${color}44`,
                                        padding: "1px 5px",
                                        borderRadius: 3,
                                        verticalAlign: "middle",
                                      }}
                                    >
                                      {esCot ? `cot ${cotN}` : it.pUnitFromCatalog ? "auto" : "manual"}
                                    </span>
                                  );
                                })()}
                                {it.pUnit > 0 && (() => {
                                  // M5 (ago-26): snapshot con edad — sobre 60 días
                                  // se marca naranjo para que se revise antes de
                                  // facturar con un precio viejo.
                                  const dias = co.createdAt
                                    ? Math.floor((Date.now() - new Date(co.createdAt).getTime()) / 86400000)
                                    : 0;
                                  const vencido = dias > 60;
                                  return (
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: vencido ? "#f39c12" : "#aaa",
                                        marginTop: 2,
                                        fontStyle: "italic",
                                        fontWeight: vencido ? 700 : 400,
                                      }}
                                      title={vencido
                                        ? `Snapshot de hace ${dias} días — revisar el precio antes de facturar (no se actualiza solo si cambió el catálogo).`
                                        : "Precio al momento de ingresar la OC. Snapshot histórico — no se actualiza si cambia el catálogo."}
                                    >
                                      {vencido ? `⚠ precio de hace ${dias} días` : `válido al ${fmtDate(co.createdAt)}`}
                                    </div>
                                  );
                                })()}
                              </td>
                              <td
                                style={{
                                  ...tableTD,
                                  textAlign: "right",
                                  color: it.total ? "#2ecc71" : "#666",
                                  fontWeight: it.total ? 700 : 400,
                                }}
                              >
                                {it.total ? fmtCLP(it.total) : "—"}
                              </td>
                              <td style={{ ...tableTD, textAlign: "right" }}>
                                {(() => {
                                  const total = Number(it.qty) || 0;
                                  const del = Number(it.qtyDelivered) || 0;
                                  const inv = Number(it.qtyInvoiced) || 0;
                                  const pending = Math.max(0, total - del - inv);
                                  const fullyClosed = pending === 0 && total > 0;
                                  if (!it.piId) {
                                    return <span style={{ color: "#999", fontSize: 10 }}>—</span>;
                                  }
                                  if (fullyClosed) {
                                    return (
                                      <span style={{
                                        fontSize: 10, color: inv >= total ? "#2ecc71" : "#3498db",
                                        fontWeight: 700,
                                      }}>
                                        ✓ {inv >= total ? "Facturado" : "Entregado"}
                                      </span>
                                    );
                                  }
                                  return (
                                    <div style={{ display:"flex", flexDirection:"column", gap: 4, alignItems:"flex-end" }}>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPartialTarget({ co, focusItemId: it.piId });
                                        }}
                                        style={{
                                          background:"#1e1535", color:"#a78bfa",
                                          border:"1px solid #3a2570", borderRadius: 4,
                                          padding:"3px 8px", fontSize: 10, fontWeight: 600,
                                          cursor:"pointer", whiteSpace:"nowrap",
                                        }}
                                        title="Registrar entrega o factura parcial (acepta multi-ítem)"
                                      >
                                        + cierre parcial
                                      </button>
                                      {(del > 0 || inv > 0) && (
                                        <div style={{ fontSize: 9, color: "#aaa" }}>
                                          {del > 0 && <span style={{ color:"#3498db" }}>↗{del}g </span>}
                                          {inv > 0 && <span style={{ color:"#2ecc71" }}>✓{inv}f </span>}
                                          <span style={{ color:"#f39c12" }}>·{pending} pend</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </td>
                            </tr>,
                            // Fila adicional con deliveries existentes para este ítem.
                            // Cada delivery muestra qty + tipo + N° doc + acción "facturar"
                            // si todavía es guía pendiente de DTE.
                            (Array.isArray(it.deliveries) && it.deliveries.length > 0) && (
                              <tr key={`${idx}-deliveries`}>
                                <td colSpan={7} style={{ padding: "0 14px 8px 78px" }}>
                                  <div style={{ display:"flex", flexWrap:"wrap", gap: 6 }}>
                                    {it.deliveries.map(d => {
                                      const isGuia = d.docType === "guia";
                                      const color = isGuia ? "#3498db" : "#2ecc71";
                                      return (
                                        <div key={d._id} style={{
                                          background:"#161616", border:`1px solid ${color}33`,
                                          borderRadius: 4, padding:"4px 8px", fontSize: 10,
                                          display:"inline-flex", alignItems:"center", gap: 6,
                                        }}>
                                          <span style={{ color, fontWeight: 700 }}>
                                            {isGuia ? "📄 guía" : (d.docType === "factura" ? "✓ factura" : "✓ boleta")}
                                          </span>
                                          <span style={{ color:"#aaa", fontFamily:"monospace" }}>
                                            {d.docNumber || "—"}
                                          </span>
                                          <span style={{ color: "#aaa" }}>· {d.qty}u</span>
                                          {d.docDate && (
                                            <span style={{ color: "#aaa" }}>
                                              · {new Date(d.docDate).toLocaleDateString("es-CL", { day:"2-digit", month:"2-digit" })}
                                            </span>
                                          )}
                                          {d.linkedGuia?.number && (
                                            <span style={{ color: "#aaa", fontSize: 9, fontStyle:"italic" }}
                                              title={`Originalmente guía ${d.linkedGuia.number}`}>
                                              (← g{d.linkedGuia.number})
                                            </span>
                                          )}
                                          {isGuia && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setPromoteTarget({
                                                  workOrderId: it.workOrderId,
                                                  deliveryId: d._id,
                                                  qty: d.qty,
                                                  guiaNumber: d.docNumber,
                                                  guiaDate: d.docDate,
                                                  itemLabel: `${it.sku ? it.sku + " · " : ""}${it.name || it.desc || ""}`,
                                                });
                                              }}
                                              style={{
                                                background:"#0d2a15", color:"#2ecc71",
                                                border:"1px solid #2ecc7155", borderRadius: 6,
                                                padding:"1px 6px", fontSize: 9, fontWeight: 700,
                                                cursor:"pointer", marginLeft: 4,
                                              }}
                                              title="Registrar la factura/boleta que cubre esta guía"
                                            >
                                              + facturar
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            ),
                          ])).flat().filter(Boolean)}
                        </tbody>
                      </table>
                    )}

                    {/* M2 (ago-26): ítems YA facturados/entregados, colapsados,
                        con su documento y fecha — Cata necesita ver con qué
                        factura salió cada uno, no solo el contador. */}
                    {(co.items_done || []).length > 0 && (
                      <details style={{ marginTop: 8 }}>
                        <summary
                          style={{
                            cursor: "pointer",
                            fontSize: 11,
                            color: "#2ecc71",
                            fontWeight: 700,
                            userSelect: "none",
                          }}
                        >
                          ✓ Ya facturado / entregado ({co.items_done.length})
                        </summary>
                        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6, opacity: 0.85 }}>
                          <tbody>
                            {co.items_done.map((it, idx) => (
                              <tr key={`done-${idx}`}>
                                <td style={{ ...tableTD, width: 130, fontFamily: "monospace", color: "#3498db" }}>
                                  {it.sku || "—"}
                                </td>
                                <td style={{ ...tableTD, color: "#aaa" }}>
                                  {it.name || it.desc || "—"}
                                </td>
                                <td style={{ ...tableTD, width: 60, textAlign: "right", color: "#aaa" }}>
                                  {it.qty}u
                                </td>
                                <td style={{ ...tableTD, width: 110, textAlign: "right", color: "#aaa" }}>
                                  {fmtCLP((it.qty || 0) * (it.pUnit || 0))}
                                </td>
                                <td style={{ ...tableTD, textAlign: "right" }}>
                                  {(it.deliveries || []).length === 0 ? (
                                    <span style={{ color: "#666", fontSize: 10, fontStyle: "italic" }}>sin doc registrado</span>
                                  ) : (
                                    (it.deliveries || []).map((d, di) => (
                                      <span
                                        key={di}
                                        title={d.docDate ? `${d.docType} ${d.docNumber} · ${fmtDate(d.docDate)} · ${d.qty}u` : `${d.docType} ${d.docNumber}`}
                                        style={{
                                          display: "inline-block",
                                          marginLeft: 6,
                                          fontSize: 9.5,
                                          fontWeight: 600,
                                          color: d.docType === "factura" ? "#2ecc71" : "#3498db",
                                          background: d.docType === "factura" ? "#2ecc7118" : "#3498db18",
                                          border: `1px solid ${d.docType === "factura" ? "#2ecc71" : "#3498db"}44`,
                                          padding: "1px 6px",
                                          borderRadius: 3,
                                        }}
                                      >
                                        {d.docType === "factura" ? "F" : d.docType === "guia" ? "G" : d.docType?.[0]?.toUpperCase() || "?"} {d.docNumber}
                                        {d.docDate ? ` · ${fmtDate(d.docDate)}` : ""}
                                      </span>
                                    ))
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </details>
                    )}
                  </div>

                  {/* SECCIÓN 2: OTs taller asociadas */}
                  <div
                    style={{
                      padding: "10px 12px",
                      borderTop: "1px solid #1f1f1f",
                      background: "#0d0d0d",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        color: "#8a8a8a",
                        textTransform: "uppercase",
                        letterSpacing: ".06em",
                        marginBottom: 6,
                      }}
                    >
                      OTs taller asociadas ({(co.ots || []).length})
                    </div>
                    {(co.ots || []).length === 0 ? (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#aaa",
                          fontStyle: "italic",
                          padding: "4px 0",
                        }}
                      >
                        Sin OTs vinculadas
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th style={{ ...tableTH, width: 90 }}>N° OT</th>
                            <th style={tableTH}>Descripción</th>
                            <th style={{ ...tableTH, width: 140 }}>Estado</th>
                            <th style={{ ...tableTH, width: 130 }}>Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {co.ots.map((ot) => {
                            const isClosed = ["delivered", "facturado", "cancelled"].includes(ot.status) || !!ot.factura;
                            return (
                              <tr key={ot._id}>
                                <td
                                  style={{ ...tableTD, fontFamily: "monospace", color: "#3498db" }}
                                >
                                  N° {ot.number}
                                </td>
                                <td
                                  style={{ ...tableTD, color: "#ccc", whiteSpace: "normal" }}
                                >
                                  {ot.description || (
                                    <i style={{ color: "#999" }}>sin descripción</i>
                                  )}
                                  {/* M5 (ago-26): editor inline de descripción */}
                                  <span
                                    title="Editar descripción de la OT"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      const nueva = window.prompt(
                                        `Descripción de la OT ${ot.number}:`,
                                        ot.description || ""
                                      );
                                      if (nueva == null || nueva === (ot.description || "")) return;
                                      try {
                                        await backend.patch(`workOrder/${ot._id}`, { description: nueva.trim() });
                                        window.location.reload();
                                      } catch (err) {
                                        alert(err?.response?.data?.error || "No se pudo guardar la descripción");
                                      }
                                    }}
                                    style={{ marginLeft: 6, cursor: "pointer", color: "#3498db", fontSize: 11 }}
                                  >
                                    ✎
                                  </span>
                                </td>
                                <td style={tableTD}>
                                  <EstadoOT status={ot.status} factura={ot.factura} />
                                </td>
                                <td style={tableTD}>
                                  {isClosed ? (
                                    <button
                                      onClick={e => { e.stopPropagation(); setEditTarget(ot); }}
                                      style={{
                                        background: "#3498db22", color: "#3498db",
                                        border: "1px solid #3498db55", borderRadius: 4,
                                        padding: "3px 8px", fontSize: 10, cursor: "pointer",
                                        fontWeight: 600,
                                      }}
                                      title="Editar tipo / N° / fecha del cierre (cuadrar mes)"
                                    >
                                      ✎ Editar cierre
                                    </button>
                                  ) : (
                                    <button
                                      onClick={e => { e.stopPropagation(); setCloseTarget(ot); }}
                                      style={{
                                        background: "#2ecc7122", color: "#2ecc71",
                                        border: "1px solid #2ecc7155", borderRadius: 4,
                                        padding: "3px 8px", fontSize: 10, cursor: "pointer",
                                        fontWeight: 600,
                                      }}
                                      title="Mandar a facturar / Cerrar con guía"
                                    >
                                      ✓ Cerrar
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal cerrar/editar facturación (compartido) */}
      <CierreFacturacionModal
        target={closeTarget ? { wo: closeTarget } : editTarget ? { wo: editTarget } : null}
        onClose={() => { setCloseTarget(null); setEditTarget(null); }}
        onSaved={() => { onWorkOrderClosed?.(); setCloseTarget(null); setEditTarget(null); }}
      />

      {/* Modal cierre parcial multi-ítem (guía/factura cubre varios items) */}
      <CierreParcialModal
        target={partialTarget}
        onClose={() => setPartialTarget(null)}
        onSaved={() => { onWorkOrderClosed?.(); setPartialTarget(null); }}
        onPromoteGuia={(t) => setPromoteTarget(t)}
      />

      {/* Modal promover guía existente a factura/boleta */}
      <PromoverGuiaModal
        target={promoteTarget}
        onClose={() => setPromoteTarget(null)}
        onSaved={() => { onWorkOrderClosed?.(); setPromoteTarget(null); }}
      />
    </div>
  );
};

// ─── DetalleFacturado ──────────────────────────────────────────────────────
export const DetalleFacturado = ({ items, loading, error, totalMonto, cantidad, topCliente, onWorkOrderEdited }) => {
  const [openId, setOpenId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  if (loading && (!items || items.length === 0)) {
    return <div className="prod-expand" style={expandWrap}><div style={stateMessage()}>Cargando facturas del mes…</div></div>;
  }
  if (error) {
    return <div className="prod-expand" style={expandWrap}><div style={stateMessage("#e74c3c")}>Error: {error}</div></div>;
  }
  if (!items || items.length === 0) {
    return <div className="prod-expand" style={expandWrap}><div style={stateMessage()}>No hay facturas este mes</div></div>;
  }

  const promedio = cantidad > 0 ? totalMonto / cantidad : 0;

  const statBox = (label, value, color) => (
    <div
      style={{
        flex: 1,
        background: "#1f1f1f",
        border: "1px solid #2e2e2e",
        borderRadius: 8,
        padding: "10px 12px",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "#8a8a8a",
          textTransform: "uppercase",
          letterSpacing: ".05em",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
    </div>
  );

  return (
    <div className="prod-expand" style={expandWrap}>
      <div style={{ padding: "14px 16px", borderBottom: "1px solid #2e2e2e", background: "#1f1f1f" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" }}>
          Total facturado
        </div>
        <div style={{ fontSize: 26, fontWeight: 700, color: "#2ecc71", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
          {fmtCLP(totalMonto)}
        </div>
      </div>

      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 10,
          padding: 12,
          borderBottom: "1px solid #2e2e2e",
        }}
      >
        {statBox("Cantidad facturas", cantidad, "#fff")}
        {statBox(
          "Top cliente",
          topCliente ? `${topCliente.name} · ${fmtCLP(topCliente.monto)}` : "—",
          "#3498db"
        )}
        {statBox("Promedio", fmtCLP(promedio), "#fff")}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...tableTH, width: 28 }}></th>
              <th style={{ ...tableTH, width: 90 }}>N° OC</th>
              <th style={{ ...tableTH, width: 90 }}>Fecha cierre</th>
              <th style={tableTH}>Cliente</th>
              <th style={{ ...tableTH, width: 80 }}>OTs</th>
              <th style={tableTH}>Folio</th>
              <th style={{ ...tableTH, width: 130, textAlign: "right" }}>Monto</th>
            </tr>
          </thead>
          <tbody>
            {items.map((f) => {
              const isOpen = openId === f._id;
              const otsCount = (f.workOrderRefs || []).length;
              return (
                <React.Fragment key={f._id}>
                  <tr
                    onClick={() => setOpenId(isOpen ? null : f._id)}
                    style={{ cursor: "pointer", background: isOpen ? "#1f1f24" : "transparent" }}
                  >
                    <td style={{ ...tableTD, color: "#999", textAlign: "center" }}>
                      {isOpen ? "▼" : "▶"}
                    </td>
                    <td style={{ ...tableTD, fontFamily: "monospace", color: f.clientOCNumber ? "#3498db" : "#555" }}>
                      {f.clientOCNumber || <i>verbal</i>}
                    </td>
                    <td style={tableTD}>{f.invoicedAt ? fmtDate(f.invoicedAt) : "—"}</td>
                    <td style={{ ...tableTD, color: "#fff" }}>
                      {f.clientName || <i style={{ color: "#999" }}>—</i>}
                    </td>
                    <td style={{ ...tableTD, fontFamily: "monospace", color: "#3498db" }}>
                      {otsCount > 0 ? `${otsCount} OT${otsCount === 1 ? "" : "s"}` : "—"}
                    </td>
                    <td style={{ ...tableTD, fontFamily: "monospace", color: f.folio ? "#ccc" : "#555" }}>
                      {f.folio || <i style={{ color: "#aaa" }}>sin folio</i>}
                    </td>
                    <td style={{ ...tableTD, color: "#2ecc71", fontWeight: 700, textAlign: "right" }}>
                      {fmtCLP(f.monto)}
                    </td>
                  </tr>

                  {isOpen && (
                    <tr style={{ background: "#101010" }}>
                      <td colSpan={7} style={{ padding: "10px 14px", borderBottom: "1px solid #1f1f1f" }}>
                        <div style={{
                          fontSize: 10, color: "#8a8a8a", textTransform: "uppercase",
                          letterSpacing: ".06em", marginBottom: 6,
                        }}>
                          OTs cerradas en esta OC ({otsCount})
                        </div>
                        {otsCount === 0 ? (
                          <div style={{ fontSize: 11, color: "#aaa", display: "flex", alignItems: "center", gap: 10 }}>
                            <i>Sin OTs vinculadas</i>
                            {/* M3 (ago-26): amarre manual — Cata conoce el N° de OT */}
                            <form
                              onSubmit={async (e) => {
                                e.preventDefault();
                                const n = e.target.elements.otnum.value.trim();
                                if (!n) return;
                                try {
                                  await backend.post("produccion/vincular-ot", {
                                    customerOrderId: f._id,
                                    workOrderNumber: n,
                                  });
                                  window.location.reload();
                                } catch (err) {
                                  alert(err?.response?.data?.error || "No se pudo vincular la OT");
                                }
                              }}
                              style={{ display: "inline-flex", gap: 6, alignItems: "center" }}
                            >
                              <input
                                name="otnum"
                                placeholder="N° OT"
                                style={{
                                  width: 80, background: "#151515", color: "#eee",
                                  border: "1px solid #333", borderRadius: 5,
                                  padding: "3px 8px", fontSize: 11,
                                }}
                              />
                              <button
                                type="submit"
                                style={{
                                  background: "#0d2a15", color: "#2ecc71",
                                  border: "1px solid #0d4020", borderRadius: 5,
                                  padding: "3px 10px", fontSize: 11, cursor: "pointer", fontWeight: 700,
                                }}
                              >
                                Vincular
                              </button>
                            </form>
                          </div>
                        ) : (
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr>
                                <th style={{ ...tableTH, width: 70 }}>N° OT</th>
                                <th style={{ ...tableTH, width: 100 }}>Tipo doc</th>
                                <th style={tableTH}>Folio</th>
                                <th style={{ ...tableTH, width: 90 }}>Fecha</th>
                                <th style={{ ...tableTH, width: 110, textAlign: "right" }}>Monto</th>
                                <th style={{ ...tableTH, width: 130 }}>Acción</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(f.workOrderRefs || []).map(ot => {
                                const reverseTypeMap = {
                                  factura_electronica: "Factura",
                                  boleta: "Boleta",
                                  guia_despacho: "Guía",
                                  nota_credito: "Nota crédito",
                                };
                                const tipoLabel = ot.documentoTributario?.tipo
                                  ? reverseTypeMap[ot.documentoTributario.tipo] || ot.documentoTributario.tipo
                                  : (ot.guiaDespacho ? "Guía" : ot.factura ? "Factura" : <i style={{ color: "#aaa" }}>—</i>);
                                const folioOt = ot.factura || ot.guiaDespacho || ot.documentoTributario?.numero || null;
                                const fechaOt = ot.documentoTributario?.fecha || null;
                                const otPieces = ot.pieces || [];
                                return (
                                  <React.Fragment key={ot._id}>
                                    <tr>
                                      <td style={{ ...tableTD, fontFamily: "monospace", color: "#3498db" }}>
                                        N° {ot.number}
                                      </td>
                                      <td style={tableTD}>{tipoLabel}</td>
                                      <td style={{ ...tableTD, fontFamily: "monospace", color: folioOt ? "#ccc" : "#666" }}>
                                        {folioOt || <i>sin folio</i>}
                                      </td>
                                      <td style={tableTD}>{fechaOt ? fmtDate(fechaOt) : "—"}</td>
                                      <td style={{ ...tableTD, color: "#2ecc71", textAlign: "right" }}>
                                        {fmtCLP(ot.total)}
                                      </td>
                                      <td style={tableTD}>
                                        <button
                                          onClick={e => { e.stopPropagation(); setEditTarget(ot); }}
                                          style={{
                                            background: "#3498db22", color: "#3498db",
                                            border: "1px solid #3498db55", borderRadius: 4,
                                            padding: "3px 8px", fontSize: 10, cursor: "pointer",
                                            fontWeight: 600,
                                          }}
                                          title="Editar tipo / N° / fecha del cierre"
                                        >
                                          ✎ Editar cierre
                                        </button>
                                      </td>
                                    </tr>
                                    {/* Items facturados de esta OT */}
                                    {otPieces.length > 0 && (
                                      <tr>
                                        <td colSpan={6} style={{ padding: "4px 10px 10px 30px", background: "#0a0a0a", borderBottom: "1px solid #1f1f1f" }}>
                                          <div style={{
                                            fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase",
                                            letterSpacing: ".05em", marginBottom: 4,
                                          }}>
                                            Items facturados ({otPieces.length})
                                          </div>
                                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                            <thead>
                                              <tr>
                                                <th style={{ ...tableTH, width: 130 }}>SKU</th>
                                                <th style={tableTH}>Nombre</th>
                                                <th style={{ ...tableTH, width: 50, textAlign: "right" }}>Qty</th>
                                                <th style={{ ...tableTH, width: 100, textAlign: "right" }}>$ Unit</th>
                                                <th style={{ ...tableTH, width: 110, textAlign: "right" }}>Subtotal</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {otPieces.map((p, idx) => (
                                                <tr key={idx}>
                                                  <td style={{ ...tableTD, fontFamily: "monospace", color: p.sku ? "#3498db" : "#666" }}>
                                                    {p.sku || <i>—</i>}
                                                  </td>
                                                  <td style={{ ...tableTD, color: "#ccc", whiteSpace: "normal" }}>
                                                    {p.name || <i style={{ color: "#aaa" }}>(sin nombre)</i>}
                                                    {!p.pieceId && (
                                                      <span style={{
                                                        marginLeft: 6, fontSize: 9, color: "#a78bfa",
                                                        background: "#a78bfa18", border: "1px solid #a78bfa44",
                                                        padding: "1px 5px", borderRadius: 3, fontWeight: 700,
                                                      }}>temp</span>
                                                    )}
                                                  </td>
                                                  <td style={{ ...tableTD, textAlign: "right", color: "#aaa" }}>{p.quantity}</td>
                                                  <td style={{ ...tableTD, textAlign: "right", color: p.price ? "#aaa" : "#666" }}>
                                                    {p.price ? fmtCLP(p.price) : "—"}
                                                  </td>
                                                  <td style={{ ...tableTD, textAlign: "right", color: p.subtotal ? "#2ecc71" : "#666", fontWeight: p.subtotal ? 600 : 400 }}>
                                                    {p.subtotal ? fmtCLP(p.subtotal) : "—"}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal editar cierre (compartido) */}
      <CierreFacturacionModal
        target={editTarget ? { wo: editTarget } : null}
        onClose={() => setEditTarget(null)}
        onSaved={() => { onWorkOrderEdited?.(); setEditTarget(null); }}
      />
    </div>
  );
};

// ─── DetalleRegularizar ───────────────────────────────────────────────────
// Vista agrupada por N° de guía. Cada card representa una guía con sus items
// pendientes de facturación. Dos acciones:
//   · Aplicar OC (sólo si la CO no tiene clientOCNumber)
//   · Facturar guía completa (loop sobre todas las deliveries)
// Filtro `sub`: all | sin_oc | con_oc — se sincroniza con el chip clickeable.
export const DetalleRegularizar = ({
  items, loading, error,
  currentSub, onSubChange,
  onChanged,
}) => {
  const [openId, setOpenId]               = useState(null);
  const [aplicarTarget, setAplicarTarget] = useState(null);
  const [facturarTarget, setFacturarTarget] = useState(null);
  const [selectMode, setSelectMode]       = useState(false);
  const [selectedLines, setSelectedLines] = useState({}); // deliveryId → line obj
  const [consolidarTarget, setConsolidarTarget] = useState(null);

  // Construye el line obj que guardamos en selectedLines.
  // `qty` arranca igual al pendiente; Maxi/Catalina lo edita inline si quiere
  // facturar parcial. El modal posterior lo respeta y permite re-editarlo.
  const lineFromItem = (g, it) => ({
    piId: it.piId,
    deliveryId: it.deliveryId,
    sku: it.sku,
    name: it.name,
    pendingQty: it.pendingQty,
    qty: it.pendingQty,
    unitPrice: it.unitPrice,
    guiaNumber: g.guia,
    coId: g.customerOrder?._id || null,
    coNumber: g.customerOrder?.number || null,
    existingClientOC: g.customerOrder?.clientOCNumber || null,
    // 26-oct: la factura electrónica necesita saber que todas las líneas son del
    // mismo cliente (un DTE tiene un solo receptor) y mostrar a quién se factura.
    clientId: g.client?._id || null,
    clientName: g.client?.name || null,
  });

  const setLineQty = (deliveryId, newQty) => {
    setSelectedLines(prev => {
      if (!prev[deliveryId]) return prev;
      return { ...prev, [deliveryId]: { ...prev[deliveryId], qty: newQty } };
    });
  };

  const toggleLine = (g, it) => {
    setSelectedLines(prev => {
      const next = { ...prev };
      if (next[it.deliveryId]) delete next[it.deliveryId];
      else next[it.deliveryId] = lineFromItem(g, it);
      return next;
    });
  };
  const toggleGuia = (g) => {
    setSelectedLines(prev => {
      const next = { ...prev };
      const allSelected = g.items.every(it => next[it.deliveryId]);
      if (allSelected) {
        g.items.forEach(it => { delete next[it.deliveryId]; });
      } else {
        g.items.forEach(it => { next[it.deliveryId] = lineFromItem(g, it); });
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedLines({});
  const exitSelectMode = () => { setSelectMode(false); clearSelection(); };

  const selCount = Object.keys(selectedLines).length;
  const selMonto = Object.values(selectedLines).reduce(
    (s, l) => s + (Number(l.qty) || 0) * (l.unitPrice || 0), 0
  );
  const selQtyTotal = Object.values(selectedLines).reduce(
    (s, l) => s + (Number(l.qty) || 0), 0
  );
  const anyInvalid = Object.values(selectedLines).some(
    l => !(Number(l.qty) > 0) || Number(l.qty) > l.pendingQty
  );

  if (loading && (!items || items.length === 0)) {
    return <div className="prod-expand" style={expandWrap}><div style={stateMessage()}>Cargando guías…</div></div>;
  }
  if (error) {
    return <div className="prod-expand" style={expandWrap}><div style={stateMessage("#e74c3c")}>Error: {error}</div></div>;
  }
  if (!items || items.length === 0) {
    return (
      <div className="prod-expand" style={expandWrap}>
        <div style={stateMessage()}>
          {currentSub === "sin_oc" ? "No hay guías sin OC" :
           currentSub === "con_oc" ? "No hay guías con OC esperando factura" :
           "No hay guías por regularizar"}
        </div>
      </div>
    );
  }

  // Total y conteos para el header
  const sinOcCount = items.filter(g => !g.hasOc).length;
  const conOcCount = items.length - sinOcCount;
  const totalMonto = items.reduce((s, g) => s + (g.monto_pendiente || 0), 0);

  return (
    <div className="prod-expand" style={expandWrap}>
      {/* Header con totales + chips (mismo patrón que DetalleOCs) */}
      <div className="chips-scroll" style={filterRow}>
        <span className="chip-touch t-02" style={chipStyle(currentSub === "all", "#fff", "#a78bfa")} onClick={() => onSubChange?.("all")}>
          Todas ({items.length})
        </span>
        <span className="chip-touch t-02" style={chipStyle(currentSub === "sin_oc", "#fff", "#a78bfa")} onClick={() => onSubChange?.("sin_oc")}>
          Sin OC ({sinOcCount})
        </span>
        <span className="chip-touch t-02" style={chipStyle(currentSub === "con_oc", "#fff", "#3498db")} onClick={() => onSubChange?.("con_oc")}>
          Con OC ({conOcCount})
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "#cbd5e1", whiteSpace: "nowrap", alignSelf: "center" }}>
          pendiente · <strong style={{ color: "#a78bfa" }}>{fmtCLP(totalMonto)}</strong>
        </span>
        <button
          onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
          style={{
            marginLeft: 8,
            background: selectMode ? "#a78bfa22" : "transparent",
            border: `1px solid ${selectMode ? "#a78bfa" : "#444"}`,
            color: selectMode ? "#a78bfa" : "#cbd5e1",
            borderRadius: 5, padding: "5px 12px",
            fontSize: 11, fontWeight: 700, cursor: "pointer",
          }}
        >
          {selectMode ? "× Salir selección" : "⚡ Facturar guías"}
        </button>
      </div>

      {/* Lista de guías */}
      <div>
        {items.map(g => {
          const isOpen = openId === g.guia;
          const dias = g.diasDesdeFecha;
          const antiguo = dias != null && dias > 30;
          return (
            <div key={g.guia} style={{ borderBottom: "1px solid #1f1f1f" }}>
              {/* Header de la guía (mismo layout que OCs) */}
              <div
                onClick={() => setOpenId(isOpen ? null : g.guia)}
                style={{
                  background: isOpen ? "#1f1f24" : "#1a1a1a",
                  padding: "8px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  rowGap: 6,
                  flexWrap: "wrap", // móvil: los badges bajan de línea en vez de rebalsar
                  cursor: "pointer",
                  borderBottom: isOpen ? "1px solid #2e2e2e" : "none",
                }}
              >
                {selectMode && (
                  <input
                    type="checkbox"
                    checked={g.items.length > 0 && g.items.every(it => selectedLines[it.deliveryId])}
                    ref={el => {
                      if (!el) return;
                      const any = g.items.some(it => selectedLines[it.deliveryId]);
                      const all = g.items.every(it => selectedLines[it.deliveryId]);
                      el.indeterminate = any && !all;
                    }}
                    onChange={() => toggleGuia(g)}
                    onClick={e => e.stopPropagation()}
                    style={{ cursor: "pointer" }}
                  />
                )}
                <span style={{ fontSize: 11, color: "#999", width: 12 }}>
                  {isOpen ? "▼" : "▶"}
                </span>

                {/* Badge N° guía */}
                <span style={{
                  fontFamily: "monospace",
                  fontSize: 11, fontWeight: 700,
                  color: "#3498db",
                  background: "#3498db18",
                  border: "1px solid #3498db44",
                  padding: "2px 7px",
                  borderRadius: 4,
                  whiteSpace: "nowrap",
                }}>📄 Guía {g.guia}</span>

                {/* Badge OT (CO interna) — terminología del resto de la app */}
                {g.customerOrder?.number != null && (
                  <span style={{
                    fontFamily: "monospace",
                    fontSize: 11, fontWeight: 700,
                    color: "#f39c12",
                    background: "#f39c1218",
                    border: "1px solid #f39c1244",
                    padding: "2px 7px",
                    borderRadius: 4,
                    whiteSpace: "nowrap",
                  }}>OT {g.customerOrder.number}</span>
                )}

                {/* Badge hasOc / sin OC */}
                {g.hasOc ? (
                  <span style={{
                    fontFamily: "monospace",
                    fontSize: 11, fontWeight: 700,
                    color: "#3498db", background: "#3498db18",
                    border: "1px solid #3498db44",
                    padding: "2px 7px", borderRadius: 4,
                    whiteSpace: "nowrap",
                  }}>
                    N° OC {g.customerOrder?.clientOCNumber}
                  </span>
                ) : (
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    color: "#a78bfa", background: "#a78bfa18",
                    border: "1px solid #a78bfa44",
                    padding: "2px 7px", borderRadius: 4,
                    whiteSpace: "nowrap",
                  }}>
                    sin OC asignada
                  </span>
                )}

                {/* Cliente (col flex) */}
                <span style={{
                  flex: 1, minWidth: 0,
                  fontSize: 12, color: "#fff",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {g.client?.name || <i style={{ color: "#999" }}>sin cliente</i>}
                </span>

                {/* Fecha */}
                <span style={{ fontSize: 11, color: "#cbd5e1", whiteSpace: "nowrap" }}>
                  {g.fecha ? new Date(g.fecha).toLocaleDateString("es-CL") : "—"}
                </span>

                {/* Antigüedad (rojo si >30d) */}
                {dias != null && (
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    color: antiguo ? "#e74c3c" : "#999",
                    background: antiguo ? "#e74c3c18" : "transparent",
                    border: antiguo ? "1px solid #e74c3c44" : "1px solid transparent",
                    padding: "2px 7px", borderRadius: 4,
                    whiteSpace: "nowrap", minWidth: 50, textAlign: "center",
                  }}>
                    {dias === 0 ? "hoy" : `${dias}d`}
                  </span>
                )}

                {/* Monto pendiente */}
                <span style={{
                  fontSize: 12, fontWeight: 700, color: "#a78bfa",
                  whiteSpace: "nowrap", minWidth: 110, textAlign: "right",
                }}>
                  {fmtCLP(g.monto_pendiente)}
                </span>

                {/* Items count */}
                <span style={{
                  fontSize: 10, color: "#aaa", whiteSpace: "nowrap",
                  minWidth: 55, textAlign: "right",
                }}>
                  {g.items.length} ítem{g.items.length === 1 ? "" : "s"}
                </span>
              </div>

              {/* Detalle expandido */}
              {isOpen && (
                <div style={{ background: "#101010" }}>
                  <div style={{ padding: "10px 12px" }}>
                    <div style={{
                      fontSize: 10, color: "#8a8a8a",
                      textTransform: "uppercase", letterSpacing: ".06em",
                      marginBottom: 6,
                    }}>
                      Ítems ({g.items.length})
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          {selectMode && <th style={{ ...tableTH, width: 30 }}></th>}
                          <th style={{ ...tableTH, width: 140 }}>SKU</th>
                          <th style={tableTH}>Descripción</th>
                          <th style={{ ...tableTH, width: 70, textAlign: "right" }}>Pend.</th>
                          {selectMode && (
                            <th style={{ ...tableTH, width: 90, textAlign: "right" }}>A facturar</th>
                          )}
                          <th style={{ ...tableTH, width: 110, textAlign: "right" }}>$ Unit</th>
                          <th style={{ ...tableTH, width: 120, textAlign: "right" }}>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.items.map((it, idx) => {
                          const sel = selectedLines[it.deliveryId];
                          const isSel = !!sel;
                          const effectiveQty = isSel ? Number(sel.qty) : it.pendingQty;
                          const invalid = isSel && (!(effectiveQty > 0) || effectiveQty > it.pendingQty);
                          return (
                            <tr key={idx} style={isSel ? { background: "#a78bfa11" } : null}>
                              {selectMode && (
                                <td style={{ ...tableTD, textAlign: "center" }}>
                                  <input
                                    type="checkbox"
                                    checked={isSel}
                                    onChange={() => toggleLine(g, it)}
                                    style={{ cursor: "pointer" }}
                                  />
                                </td>
                              )}
                              <td style={{ ...tableTD, fontFamily: "monospace", color: it.sku ? "#3498db" : "#555" }}>
                                {it.sku || "—"}
                              </td>
                              <td style={{ ...tableTD, color: "#fff", whiteSpace: "normal" }}>
                                {it.name || <i style={{ color: "#999" }}>(sin descripción)</i>}
                              </td>
                              <td style={{ ...tableTD, textAlign: "right", color: "#aaa" }}>
                                {it.pendingQty}
                              </td>
                              {selectMode && (
                                <td style={{ ...tableTD, textAlign: "right" }}>
                                  {isSel ? (
                                    <input
                                      type="number"
                                      min="0"
                                      max={it.pendingQty}
                                      value={sel.qty}
                                      onChange={e => setLineQty(it.deliveryId, e.target.value === "" ? "" : Number(e.target.value))}
                                      onClick={e => e.stopPropagation()}
                                      style={{
                                        width: 56, padding: "3px 6px",
                                        background: "#0d0d0d",
                                        border: `1px solid ${invalid ? "#e74c3c" : "#444"}`,
                                        borderRadius: 3,
                                        color: invalid ? "#e74c3c" : "#fff",
                                        fontSize: 11, textAlign: "right",
                                        fontFamily: "monospace",
                                      }}
                                    />
                                  ) : <span style={{ color: "#555" }}>—</span>}
                                </td>
                              )}
                              <td style={{ ...tableTD, textAlign: "right", color: "#aaa" }}>
                                {fmtCLP(it.unitPrice)}
                              </td>
                              <td style={{ ...tableTD, textAlign: "right", color: "#fff", fontWeight: 700 }}>
                                {fmtCLP((isSel ? effectiveQty : it.pendingQty) * (it.unitPrice || 0))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Observaciones de la CO */}
                  {g.customerOrder?.observations && (
                    <div style={{ padding: "4px 12px 10px" }}>
                      <div style={{
                        fontSize: 10, color: "#8a8a8a",
                        textTransform: "uppercase", letterSpacing: ".06em",
                        marginBottom: 4,
                      }}>
                        Observaciones
                      </div>
                      <div style={{
                        fontSize: 11, color: "#aaa",
                        padding: "8px 10px",
                        background: "#0d0d0d", border: "1px solid #2e2e2e",
                        borderRadius: 4, lineHeight: 1.5, whiteSpace: "pre-wrap",
                      }}>
                        {g.customerOrder.observations}
                      </div>
                    </div>
                  )}

                  {/* Acciones */}
                  <div style={{
                    display: "flex", gap: 8, justifyContent: "flex-end",
                    padding: "8px 12px 12px", flexWrap: "wrap",
                    borderTop: "1px solid #1f1f1f",
                  }}>
                    {!g.hasOc && g.customerOrder?._id && (
                      <button
                        onClick={() => setAplicarTarget({
                          coId: g.customerOrder._id,
                          coNumber: g.customerOrder.number,
                          clientName: g.client?.name || null,
                          guiaNumber: g.guia,
                          monto: g.monto_pendiente,
                        })}
                        style={{
                          background: "transparent", border: "1px solid #a78bfa",
                          color: "#a78bfa", borderRadius: 4,
                          padding: "6px 14px", fontSize: 11, fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        + Aplicar N° OC
                      </button>
                    )}
                    {g.workOrderId && g.items.length > 0 && (
                      <button
                        onClick={() => setFacturarTarget({
                          guiaNumber: g.guia,
                          guiaDate: g.fecha,
                          coNumber: g.customerOrder?.number,
                          clientName: g.client?.name || null,
                          monto: g.monto_pendiente,
                          deliveries: g.items.map(it => ({
                            workOrderId: g.workOrderId,
                            deliveryId: it.deliveryId,
                            qty: it.pendingQty,
                            itemLabel: `${it.sku || ""} · ${it.name || ""}`.trim(),
                          })),
                        })}
                        style={{
                          background: "#2ecc71", border: "none",
                          color: "#0d2a15", borderRadius: 4,
                          padding: "6px 14px", fontSize: 11, fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Facturar guía completa
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer flotante de selección consolidada */}
      {selectMode && selCount > 0 && (
        <div style={{
          position: "fixed",
          bottom: 20,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#1a1a1a",
          border: "1px solid #a78bfa",
          borderRadius: 8,
          padding: "10px 16px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", gap: 14,
          flexWrap: "wrap", // móvil: que el footer no rebalse el viewport
          maxWidth: "94vw",
          zIndex: 100,
        }}>
          <div style={{ fontSize: 12, color: "#fff" }}>
            <strong style={{ color: "#a78bfa" }}>{selCount}</strong> línea{selCount === 1 ? "" : "s"} ·{" "}
            <strong>{selQtyTotal}</strong> unidad{selQtyTotal === 1 ? "" : "es"} ·{" "}
            <strong>{fmtCLP(selMonto)}</strong>
          </div>
          {anyInvalid && (
            <span style={{ fontSize: 11, color: "#e74c3c" }}>
              ⚠ Hay cantidades inválidas
            </span>
          )}
          <button
            onClick={clearSelection}
            style={{
              background: "transparent", border: "1px solid #444",
              color: "#aaa", borderRadius: 4,
              padding: "5px 10px", fontSize: 11, cursor: "pointer",
            }}
          >
            Limpiar
          </button>
          <button
            onClick={() => setConsolidarTarget({ lines: Object.values(selectedLines) })}
            disabled={anyInvalid}
            style={{
              background: anyInvalid ? "#444" : "#2ecc71", border: "none",
              color: "#0d2a15", borderRadius: 4,
              padding: "6px 16px", fontSize: 12, fontWeight: 700,
              cursor: anyInvalid ? "not-allowed" : "pointer",
              opacity: anyInvalid ? 0.6 : 1,
            }}
          >
            Facturar →
          </button>
        </div>
      )}

      {/* Modales */}
      <AplicarOCModal
        target={aplicarTarget}
        onClose={() => setAplicarTarget(null)}
        onSaved={() => { setAplicarTarget(null); onChanged?.(); }}
      />
      <FacturarGuiaCompletaModal
        target={facturarTarget}
        onClose={() => setFacturarTarget(null)}
        onSaved={() => { setFacturarTarget(null); onChanged?.(); }}
      />
      <FacturarModal
        fuente="regularizar"
        target={consolidarTarget}
        onClose={() => setConsolidarTarget(null)}
        onSaved={() => {
          setConsolidarTarget(null);
          exitSelectMode();
          onChanged?.();
        }}
      />
    </div>
  );
};
